import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Target, FolderKanban, BookOpen, Feather, Building2, Mail, Newspaper, Users, FileBarChart, Megaphone, Presentation, ShoppingBag, HelpCircle, GraduationCap, Shield, Clock, Edit } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import PageAddToProjectButton from '@/components/private-text-generator/PageAddToProjectButton';

interface PageType {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  description: string;
}

const pageTypes: PageType[] = [
  {
    id: 'Blog Post',
    name: 'Blog Post',
    icon: FileText,
    color: 'blue',
    bgColor: 'bg-blue-50/80 dark:bg-blue-950/30',
    borderColor: 'border-blue-200/60 dark:border-blue-800/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    description: 'Engaging articles for blogs and websites'
  },
  {
    id: 'Article',
    name: 'Article',
    icon: BookOpen,
    color: 'emerald',
    bgColor: 'bg-emerald-50/80 dark:bg-emerald-950/30',
    borderColor: 'border-emerald-200/60 dark:border-emerald-800/40',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    description: 'In-depth informational content'
  },
  {
    id: 'Social Media Post',
    name: 'Social Media Post',
    icon: Target,
    color: 'orange',
    bgColor: 'bg-orange-50/80 dark:bg-orange-950/30',
    borderColor: 'border-orange-200/60 dark:border-orange-800/40',
    textColor: 'text-orange-700 dark:text-orange-300',
    description: 'Short-form content for social platforms'
  },
  {
    id: 'Marketing Copy',
    name: 'Marketing Copy',
    icon: Target,
    color: 'purple',
    bgColor: 'bg-purple-50/80 dark:bg-purple-950/30',
    borderColor: 'border-purple-200/60 dark:border-purple-800/40',
    textColor: 'text-purple-700 dark:text-purple-300',
    description: 'Persuasive marketing and promotional content'
  },
  {
    id: 'Strategic Plan',
    name: 'Strategic Plan',
    icon: FolderKanban,
    color: 'pink',
    bgColor: 'bg-pink-50/80 dark:bg-pink-950/30',
    borderColor: 'border-pink-200/60 dark:border-pink-800/40',
    textColor: 'text-pink-700 dark:text-pink-300',
    description: 'Strategic planning and business documents'
  },
  {
    id: 'Email',
    name: 'Email',
    icon: Mail,
    color: 'indigo',
    bgColor: 'bg-indigo-50/80 dark:bg-indigo-950/30',
    borderColor: 'border-indigo-200/60 dark:border-indigo-800/40',
    textColor: 'text-indigo-700 dark:text-indigo-300',
    description: 'Email campaigns and communications'
  },
  {
    id: 'Newsletter',
    name: 'Newsletter',
    icon: Newspaper,
    color: 'teal',
    bgColor: 'bg-teal-50/80 dark:bg-teal-950/30',
    borderColor: 'border-teal-200/60 dark:border-teal-800/40',
    textColor: 'text-teal-700 dark:text-teal-300',
    description: 'Regular newsletters and updates'
  },
  {
    id: 'Script',
    name: 'Script',
    icon: Feather,
    color: 'cyan',
    bgColor: 'bg-cyan-50/80 dark:bg-cyan-950/30',
    borderColor: 'border-cyan-200/60 dark:border-cyan-800/40',
    textColor: 'text-cyan-700 dark:text-cyan-300',
    description: 'Scripts for videos, podcasts, and presentations'
  },
  {
    id: 'Case Study',
    name: 'Case Study',
    icon: Building2,
    color: 'orange',
    bgColor: 'bg-orange-50/80 dark:bg-orange-950/30',
    borderColor: 'border-orange-200/60 dark:border-orange-800/40',
    textColor: 'text-orange-700 dark:text-orange-300',
    description: 'Detailed business case studies'
  },
  {
    id: 'Event Plan',
    name: 'Event Plan',
    icon: Users,
    color: 'pink',
    bgColor: 'bg-pink-50/80 dark:bg-pink-950/30',
    borderColor: 'border-pink-200/60 dark:border-pink-800/40',
    textColor: 'text-pink-700 dark:text-pink-300',
    description: 'Event planning and coordination documents'
  },
  {
    id: 'Standard Document',
    name: 'Standard Document',
    icon: FileText,
    color: 'neutral',
    bgColor: 'bg-neutral-50/80 dark:bg-neutral-950/30',
    borderColor: 'border-neutral-200/60 dark:border-neutral-800/40',
    textColor: 'text-neutral-700 dark:text-neutral-300',
    description: 'General purpose content pages'
  },
  {
    id: 'Whitepaper',
    name: 'Whitepaper',
    icon: FileText,
    color: 'slate',
    bgColor: 'bg-slate-50/80 dark:bg-slate-950/30',
    borderColor: 'border-slate-200/60 dark:border-slate-800/40',
    textColor: 'text-slate-700 dark:text-slate-300',
    description: 'Comprehensive technical documents'
  },
  {
    id: 'Press Release',
    name: 'Press Release',
    icon: Megaphone,
    color: 'red',
    bgColor: 'bg-red-50/80 dark:bg-red-950/30',
    borderColor: 'border-red-200/60 dark:border-red-800/40',
    textColor: 'text-red-700 dark:text-red-300',
    description: 'Press releases and announcements'
  },
  {
    id: 'Proposal',
    name: 'Proposal',
    icon: FileText,
    color: 'violet',
    bgColor: 'bg-violet-50/80 dark:bg-violet-950/30',
    borderColor: 'border-violet-200/60 dark:border-violet-800/40',
    textColor: 'text-violet-700 dark:text-violet-300',
    description: 'Business proposals and project pitches'
  },
  {
    id: 'Report',
    name: 'Report',
    icon: FileBarChart,
    color: 'amber',
    bgColor: 'bg-amber-50/80 dark:bg-amber-950/30',
    borderColor: 'border-amber-200/60 dark:border-amber-800/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    description: 'Detailed reports and analysis'
  },
  {
    id: 'Presentation',
    name: 'Presentation',
    icon: Presentation,
    color: 'rose',
    bgColor: 'bg-rose-50/80 dark:bg-rose-950/30',
    borderColor: 'border-rose-200/60 dark:border-rose-800/40',
    textColor: 'text-rose-700 dark:text-rose-300',
    description: 'Presentation slides and decks'
  },
  {
    id: 'Product Description',
    name: 'Product Description',
    icon: ShoppingBag,
    color: 'lime',
    bgColor: 'bg-lime-50/80 dark:bg-lime-950/30',
    borderColor: 'border-lime-200/60 dark:border-lime-800/40',
    textColor: 'text-lime-700 dark:text-lime-300',
    description: 'Product descriptions and specifications'
  },
  {
    id: 'Landing Page',
    name: 'Landing Page',
    icon: Target,
    color: 'sky',
    bgColor: 'bg-sky-50/80 dark:bg-sky-950/30',
    borderColor: 'border-sky-200/60 dark:border-sky-800/40',
    textColor: 'text-sky-700 dark:text-sky-300',
    description: 'Landing page content and copy'
  },
  {
    id: 'FAQ',
    name: 'FAQ',
    icon: HelpCircle,
    color: 'emerald',
    bgColor: 'bg-emerald-50/80 dark:bg-emerald-950/30',
    borderColor: 'border-emerald-200/60 dark:border-emerald-800/40',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    description: 'Frequently asked questions'
  },
  {
    id: 'Tutorial',
    name: 'Tutorial',
    icon: GraduationCap,
    color: 'blue',
    bgColor: 'bg-blue-50/80 dark:bg-blue-950/30',
    borderColor: 'border-blue-200/60 dark:border-blue-800/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    description: 'Step-by-step tutorials and guides'
  },
  {
    id: 'Policy Document',
    name: 'Policy Document',
    icon: Shield,
    color: 'gray',
    bgColor: 'bg-gray-50/80 dark:bg-gray-950/30',
    borderColor: 'border-gray-200/60 dark:border-gray-800/40',
    textColor: 'text-gray-700 dark:text-gray-300',
    description: 'Policies, procedures, and guidelines'
  },
  {
    id: 'Other',
    name: 'Other',
    icon: FileText,
    color: 'neutral',
    bgColor: 'bg-neutral-50/80 dark:bg-neutral-950/30',
    borderColor: 'border-neutral-200/60 dark:border-neutral-800/40',
    textColor: 'text-neutral-700 dark:text-neutral-300',
    description: 'Custom content type'
  }
];

interface PageCardProps {
  page: {
    id: string;
    title: string;
    type: string;
    updated_at: string;
    content?: string;
    workbook_id?: string | null; // NEW: Add workbook_id for project detection
  };
  onEdit: (pageId: string) => void;
  hideProjectButton?: boolean; // NEW: Optional prop to hide Add to Project button
}

const PageCard: React.FC<PageCardProps> = ({ page, onEdit, hideProjectButton = false }) => {
  // Get the page type configuration
  const currentPageType = pageTypes.find(type => type.id === page.type);

  // Format the updated date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Calculate basic content statistics
  const wordCount = page.content ? page.content.split(/\s+/).filter(word => word.length > 0).length : 0;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="group cursor-pointer"
      onClick={() => onEdit(page.id)}
    >
      {currentPageType ? (
        <div className={cn(
          "relative overflow-hidden rounded-xl h-full flex flex-col",
          currentPageType.bgColor,
          currentPageType.borderColor,
          "border-2 shadow-sm"
        )}>
          {/* Subtle top accent */}
          <div className={cn(
            "absolute top-0 left-0 right-0 h-0.5",
            `bg-gradient-to-r from-${currentPageType.color}-400 to-${currentPageType.color}-500`
          )} />
          
          <div className="p-4 flex flex-col h-full">
            {/* Header with page title */}
            <div className="space-y-3 flex-1">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-lg leading-tight text-gray-900 dark:text-gray-100">
                  {page.title || 'Untitled Document'}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 p-1 h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(page.id);
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Metadata moved from footer to header */}
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Clock className="h-3 w-3" />
                <span>{formatDate(page.updated_at)}</span>
                <span className="mx-1">•</span>
                <FileText className="h-3 w-3" />
                <span>{wordCount} words • {readingTime}m read</span>
              </div>
            </div>
            
            {/* Footer with page type and action buttons */}
            <div className="pt-3 mt-3 border-t border-gray-200/50 dark:border-gray-700/50">
              {/* Bottom section with page type on left and action buttons on right */}
              <div className="flex items-end justify-between">
                {/* Page type moved from header to footer */}
                <h4 className={cn(
                  "font-medium text-sm",
                  currentPageType.textColor
                )}>
                  {currentPageType.name}
                </h4>
                
                {/* Action buttons on the right side */}
                {!hideProjectButton && (
                  <div className="flex items-center gap-2">
                    <PageAddToProjectButton
                      pageId={page.id}
                      dropdownLabel="Add to Project"
                      variant="auto"
                      currentProjectId={page.workbook_id}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Fallback for pages without type */
        <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 h-full flex flex-col">
          <div className="space-y-3 flex-1">
            <div className="flex items-start justify-between">
              <h3 className="font-semibold text-lg leading-tight text-gray-900 dark:text-gray-100">
                {page.title || 'Untitled Document'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 p-1 h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(page.id);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Metadata moved from footer to header */}
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="h-3 w-3" />
              <span>{formatDate(page.updated_at)}</span>
              <span className="mx-1">•</span>
              <FileText className="h-3 w-3" />
              <span>{wordCount} words • {readingTime}m read</span>
            </div>
          </div>
          
          {/* Footer with page type and action buttons */}
          <div className="pt-3 mt-3 border-t border-gray-200/50 dark:border-gray-700/50">
            {/* Bottom section with page type on left and action buttons on right */}
            <div className="flex items-end justify-between">
              {/* Page type moved from header to footer */}
              <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                Standard Document
              </h4>
              
              {/* Action buttons on the right side */}
              {!hideProjectButton && (
                <div className="flex items-center gap-2">
                  <PageAddToProjectButton
                    pageId={page.id}
                    dropdownLabel="Add to Project"
                    variant="auto"
                    currentProjectId={page.workbook_id}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PageCard; 